import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import classNames from "classnames"

import {
  useLazyGetSameParentCompaniesQuery,
  useLazyPutContractorsDevicesAsTrasferedQuery,
} from "store/api/contractorsApi"
import { useAppSelector } from "store/hooks"
import { UITypes } from "types"
import { ReactComponent as IconTransfer } from "assets/svg/modal-icon-trasfer.svg"

import getErrorMessage from "utils/getErrorMessage"
import { ModalLayout } from "layouts/ModalLayout"
import { SelectStatic } from "components/form/SelectStatic"

import styles from "./index.module.scss"

interface Props {
  onCancel: () => void
  onSuccess: () => void
  skipVendor: boolean
}

export const TransferModal = ({ onCancel, onSuccess, skipVendor }: Props) => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const nuveCompany = { value: "", label: "Nuve" }
  const [selectedContractor, setSelectedContractor] = useState<UITypes.Option[]>([])
  const [triggerPutContractorsDevicesAsTransferred, { isFetching }] = useLazyPutContractorsDevicesAsTrasferedQuery()
  const selectedCustomerDevices = useAppSelector(state => state.tempData.selectedCustomerDevices)
  const [triggerGetSameParentCompanies, { data: sameParentCompanies }] = useLazyGetSameParentCompaniesQuery()

  const options = useMemo(() => {
    return [
      ...(skipVendor ? [] : [nuveCompany]),
      ...(sameParentCompanies?.data.map(item => ({
        value: item.id,
        label: item.name,
      })) || []),
    ]
  }, [sameParentCompanies])

  useEffect(() => {
    if (userProfile?.contractor?.parent_company) {
      triggerGetSameParentCompanies()
    }
  }, [])

  useEffect(() => {
    if (sameParentCompanies?.data.length === 0 || !userProfile?.contractor?.parent_company) {
      setSelectedContractor([nuveCompany])
    }
  }, [sameParentCompanies])

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    try {
      const res = await triggerPutContractorsDevicesAsTransferred({
        device_ids: selectedCustomerDevices.map(item => item.id),
        contractor_id: selectedContractor[0].value as string,
      })
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      } else {
        toast.success("The status of the selected thermostat(s) was changed successfully!")
        onSuccess()
      }
    } catch (error) {
      console.error(error)
    }
  }

  if (!userProfile) {
    return null
  }

  const getDescription = () => {
    if (userProfile.contractor?.parent_company) {
      return `Are you sure you want to transfer the ${selectedCustomerDevices.length} thermostat(s) to the selected Contractor or Nuve?`
    }
    return `By transferring the selected ${selectedCustomerDevices.length} thermostat(s) to the Vendor, you give away the responsibility for their future support. The transferred thermostat(s) will be deducted from your Inventory and Total Thermostats. Are you sure you want to transfer the selected thermostat(s)?`
  }

  return (
    <>
      {createPortal(
        <ModalLayout
          width="450px"
          isFetching={isFetching}
          isDisabled={selectedContractor.length === 0}
          onConfirm={onSubmit}
          onCancel={onCancel}
        >
          <div className={classNames(styles.modalContent, styles.center)}>
            <IconTransfer />
            <h3 className={styles.modalTitle}>Transfer</h3>
            <div className={styles.modalMessage}>{getDescription()}</div>
            {userProfile.contractor?.parent_company && (
              <div className={styles.selectWrapper}>
                <SelectStatic
                  options={options}
                  values={selectedContractor}
                  onChange={setSelectedContractor}
                  placeholder="Select Contractor or Nuve"
                />
              </div>
            )}
          </div>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
